import React from "react";
import { cn } from "@/lib/utils";
import CTAButton from "./CTAButton";
import "./page-hero.css";

export const PageHero = ({
  id,
  sectionRef,
  eyebrow,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  variant = "dark",
  image,
  imageAlt = "",
  imagePosition = "center",
  footerContent = null,
  compact = false,
  className = "",
  asideContent = null,
}) => {
  const isDark = variant === "dark";

  return (
    <section
      ref={sectionRef}
      id={id}
      className={cn(
        "page-hero relative overflow-hidden",
        image && "page-hero--with-image",
        compact && "page-hero--compact",
        isDark ? "page-hero--dark bg-gradient-hero text-white" : "page-hero--light bg-gradient-soft text-foreground",
        className
      )}
    >
      {image ? (
        <>
          <img
            src={image}
            alt={imageAlt}
            className="page-hero__image absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: imagePosition }}
          />
          <div className="page-hero__image-wash absolute inset-0 pointer-events-none" />
        </>
      ) : null}

      <div className="page-hero__container relative container-px mx-auto max-w-7xl">
        <div className={cn("page-hero__layout", asideContent && "page-hero__layout--split")}>
          <div className="page-hero__content max-w-3xl animate-fade-up">
            {eyebrow ? (
              <span
                className={cn(
                  "inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase",
                  isDark ? "text-[hsl(var(--cyan))]" : "text-primary"
                )}
              >
                <span className="inline-block w-6 h-px bg-current opacity-70" />
                {eyebrow}
              </span>
            ) : null}

            <h1
              className={cn(
                "mt-4 font-display font-semibold text-balance text-4xl sm:text-5xl lg:text-6xl leading-[1.06]",
                isDark ? "text-white" : "text-foreground"
              )}
            >
              {title}
            </h1>

            {subtitle ? (
              <p
                className={cn(
                  "mt-6 text-base lg:text-lg leading-relaxed max-w-2xl text-balance",
                  isDark ? "text-white/78" : "text-muted-foreground"
                )}
              >
                {subtitle}
              </p>
            ) : null}

            {primaryCta || secondaryCta ? (
              <div className="page-hero__actions mt-9 flex flex-wrap gap-3">
                {primaryCta ? (
                  <CTAButton {...primaryCta} variant="primary" size="lg" />
                ) : null}

                {secondaryCta ? (
                  <CTAButton
                    {...secondaryCta}
                    variant={isDark ? "outline" : "solidDark"}
                    size="lg"
                    icon={false}
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          {asideContent ? (
            <div className="page-hero__aside animate-fade-up">{asideContent}</div>
          ) : null}
        </div>

        {footerContent ? (
          <div className="page-hero__footer relative z-[5]">{footerContent}</div>
        ) : null}
      </div>

      {isDark ? (
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--cyan))]/40 to-transparent" />
      ) : null}
    </section>
  );
};

export default PageHero;
